import { Combobox } from '@base-ui/react/combobox'
import React from 'react'
import styled from 'styled-components'
import {
  filterOptions,
  optionText,
} from 'sutra/components/Select/select-options'
import {
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxPositioner,
  NoResults,
  SearchInput,
  type SelectSize,
  triggerStyles,
} from 'sutra/components/Select/select-styles'
import type {
  SelectOption,
  SelectValue,
} from 'sutra/components/Select/select-types'
import { SelectTriggerContent } from 'sutra/components/Select/SelectAdornments'
import { OptionContent } from 'sutra/components/Select/SelectOptionRow'
import {
  type SingleSelectProps,
  useSelectSingle,
} from 'sutra/components/Select/use-select-single'

const ComboboxTrigger = styled(Combobox.Trigger)<{ $size: SelectSize }>`
  ${triggerStyles}
`

// A single-value dropdown with a type-to-filter input as the first row of the
// panel, on Base UI's Combobox. Filtering is done here (`filter={null}` hands
// Base UI the already-filtered list) so `filterOption`, `filterSort` and a
// caller-owned `searchValue` all behave the same way.
export function SelectSearchable<
  T extends SelectValue,
  O extends SelectOption<T>,
>(props: SingleSelectProps<T, O>): React.ReactElement {
  const state = useSelectSingle(props)
  const { size = 'sm', zIndex = 1000, panelAlign = 'start' } = props
  const visible = filterOptions(state.all, {
    query: state.search.query,
    filterOption: props.filterOption,
    filterSort: props.filterSort,
    optionFilterProp: props.optionFilterProp,
  })

  return (
    <Combobox.Root
      items={visible}
      filter={null}
      value={state.selectedOption ?? null}
      onValueChange={(next) => {
        if (next && !next.disabled) state.commit(next.value)
      }}
      itemToStringLabel={(option) => optionText(option, props.optionFilterProp)}
      isItemEqualToValue={(a, b) => a.value === b.value}
      inputValue={state.search.query}
      onInputValueChange={(text, details) =>
        state.search.setQuery(text, details.reason)
      }
      open={state.isOpen}
      onOpenChange={state.handleOpenChange}
      disabled={props.disabled}
    >
      <ComboboxTrigger $size={size} {...state.triggerProps}>
        <SelectTriggerContent
          labelId={state.labelId}
          label={state.triggerLabel}
          showPlaceholder={state.showPlaceholder}
          icon={props.triggerIcon}
          accessory={props.triggerAccessory}
          onClear={state.showClear ? state.clear : undefined}
          loading={props.loading}
          suffixIcon={props.suffixIcon}
        />
      </ComboboxTrigger>
      <Combobox.Portal>
        <ComboboxPositioner
          side="bottom"
          align={panelAlign}
          sideOffset={4}
          $zIndex={zIndex}
        >
          <ComboboxPopup
            className={props.popupClassName}
            $popupWidth={props.popupMatchSelectWidth}
          >
            <SearchInput aria-label="Search" />
            <NoResults>{state.emptyText}</NoResults>
            <ComboboxList>
              {(option: O) => (
                <ComboboxItem
                  key={String(option.value)}
                  value={option}
                  disabled={option.disabled}
                >
                  <OptionContent
                    option={option}
                    optionRender={props.optionRender}
                  />
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxPopup>
        </ComboboxPositioner>
      </Combobox.Portal>
    </Combobox.Root>
  )
}
